import { ArrowUpDown, MoreHorizontal } from 'lucide-react';
import { ReactNode } from 'react';

export interface Column<T> {
  key: string;
  label: string;
  sortable?: boolean;
  hasMenu?: boolean;
  render?: (item: T, index: number) => ReactNode;
  className?: string;
}

export interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  onRowClick?: (item: T) => void;
  selectedRows?: Set<number>;
  onRowSelect?: (index: number) => void;
  showCheckbox?: boolean;
  hoverable?: boolean;
  striped?: boolean;
}

export function DataTable<T extends Record<string, any>>({
  data,
  columns,
  onRowClick,
  selectedRows = new Set(),
  onRowSelect,
  showCheckbox = false,
  hoverable = true,
  striped = true,
}: DataTableProps<T>) {
  const handleRowClick = (item: T, e: React.MouseEvent) => {
    // Don't trigger row click if clicking on checkbox or button
    const target = e.target as HTMLElement;
    if (
      target.closest('button') ||
      target.closest('input[type="checkbox"]')
    ) {
      return;
    }
    onRowClick?.(item);
  };

  return (
    <div className="overflow-x-auto overflow-y-auto -mx-4 md:-mx-6 flex-1">
      <table className="w-full min-w-[640px]">
        <thead>
          <tr className="border-b border-gray-100">
            {showCheckbox && (
              <th className="px-3 md:px-6 py-2 md:py-3 text-left w-10 md:w-12 align-middle">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-gray-400 focus:ring-gray-400"
                />
              </th>
            )}
            {columns.map((column) => (
              <th key={column.key} className={`px-3 md:px-6 py-2 md:py-3 text-left align-middle ${column.className || ''}`}>
                <div className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm font-medium text-gray-600 whitespace-nowrap">
                  {column.label}
                  {column.sortable && (
                    <ArrowUpDown className="w-3 h-3 md:w-3.5 md:h-3.5 text-gray-400 cursor-pointer hover:text-gray-600" />
                  )}
                  {column.hasMenu && (
                    <MoreHorizontal className="w-3 h-3 md:w-3.5 md:h-3.5 text-[#FF6B35] cursor-pointer" />
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => {
            const isSelected = selectedRows.has(index);
            const rowBgClass = striped
              ? index % 2 === 0
                ? 'bg-gray-50/30'
                : 'bg-white'
              : 'bg-white';

            return (
              <tr
                key={index}
                className={`border-b border-gray-50 last:border-b-0 ${rowBgClass} ${
                  hoverable ? 'hover:bg-gray-100/50 cursor-pointer' : ''
                } ${isSelected ? 'ring-2 ring-[#FF6B35] ring-inset' : ''} transition-all`}
                onClick={(e) => handleRowClick(item, e)}
              >
                {showCheckbox && (
                  <td className="px-3 md:px-6 py-3 md:py-4 align-middle">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onRowSelect?.(index)}
                      className="w-4 h-4 rounded border-gray-300 text-gray-400 focus:ring-gray-400"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                )}
                {columns.map((column) => (
                  <td key={column.key} className={`px-3 md:px-6 py-3 md:py-4 align-middle text-start ${column.className || ''}`}>
                    {column.render ? (
                      column.render(item, index)
                    ) : (
                      <span className="text-sm text-gray-900 text-start">
                        {item[column.key]}
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default DataTable;
